import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { $getSelection, $isRangeSelection } from 'lexical';
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
} from '@lexical/selection';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { richTextEditorStrings } from '../../../i18n/components/richtTextEditor';

const { colors } = richTextEditorStrings.textColor;

// Dark text colors and light highlights stay legible on screen and in print.
const TEXT_COLORS = [
  { name: colors.red, value: '#c62828' },
  { name: colors.orange, value: '#e65100' },
  { name: colors.green, value: '#2e7d32' },
  { name: colors.blue, value: '#1565c0' },
];

const HIGHLIGHT_COLORS = [
  { name: colors.yellow, value: '#fff176' },
  { name: colors.green, value: '#c8e6c9' },
  { name: colors.blue, value: '#bbdefb' },
  { name: colors.red, value: '#ffcdd2' },
];

type ColorStyle = { color?: string | null; 'background-color'?: string | null };

// Keeps the editor focused so the text selection survives the click.
const keepEditorSelection = (event: MouseEvent) => event.preventDefault();

export function TextColorPlugin() {
  const [editor] = useLexicalComposerContext();
  const overlayRef = useRef<OverlayPanel>(null);
  const [textColor, setTextColor] = useState('');
  const [highlightColor, setHighlightColor] = useState('');

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }
        setTextColor($getSelectionStyleValueForProperty(selection, 'color'));
        setHighlightColor(
          $getSelectionStyleValueForProperty(selection, 'background-color'),
        );
      });
    });
  }, [editor]);

  const applyStyle = useCallback(
    (style: ColorStyle) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, style);
        }
      });
      overlayRef.current?.hide();
    },
    [editor],
  );

  const isColored = textColor !== '' || highlightColor !== '';

  return (
    <span className="lexical-toolbar-group">
      <button
        type="button"
        onMouseDown={keepEditorSelection}
        onClick={(event) => overlayRef.current?.toggle(event)}
        className={`lexical-toolbar-button ${isColored ? 'active' : ''}`}
        aria-label={richTextEditorStrings.textColor.button.label}
        title={richTextEditorStrings.textColor.button.title}
        aria-haspopup="dialog"
      >
        <span
          className="lexical-text-color-icon"
          style={{
            borderBottomColor: textColor || 'currentColor',
            backgroundColor: highlightColor || undefined,
          }}
        >
          A
        </span>
      </button>
      <OverlayPanel ref={overlayRef} dismissable>
        <div
          className="lexical-text-color-content"
          role="dialog"
          aria-label={richTextEditorStrings.textColor.dialog.label}
        >
          <span className="lexical-color-picker-title">
            {richTextEditorStrings.textColor.textColor}
          </span>
          <div className="lexical-text-color-row">
            {TEXT_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                className="lexical-text-color-swatch"
                style={{ color: color.value }}
                onMouseDown={keepEditorSelection}
                onClick={() => applyStyle({ color: color.value })}
                aria-label={richTextEditorStrings.textColor.setTextColorLabel(
                  color.name,
                )}
                title={color.name}
              >
                A
              </button>
            ))}
          </div>
          <span className="lexical-color-picker-title">
            {richTextEditorStrings.textColor.highlight}
          </span>
          <div className="lexical-text-color-row">
            {HIGHLIGHT_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                className="lexical-text-color-swatch"
                style={{ backgroundColor: color.value }}
                onMouseDown={keepEditorSelection}
                onClick={() => applyStyle({ 'background-color': color.value })}
                aria-label={richTextEditorStrings.textColor.setHighlightLabel(
                  color.name,
                )}
                title={color.name}
              >
                A
              </button>
            ))}
          </div>
          <Button
            type="button"
            onMouseDown={keepEditorSelection}
            onClick={() =>
              applyStyle({ color: null, 'background-color': null })
            }
            label={richTextEditorStrings.textColor.clear}
            icon="pi pi-times"
            outlined
            size="small"
          />
        </div>
      </OverlayPanel>
    </span>
  );
}
